import { env } from 'next-runtime-env';

// Utility to load federated styles from aga-components via CSS manifest
let stylesLoaded = false;

export type ExtraCssSource = {
  type: 'css-file';
  url: string;
  crossOrigin?: string;
};

export const loadFederatedStyles = async (options?: {
  baseUrl?: string;
  additionalStyles?: ExtraCssSource[];
  preserveOrder?: boolean;
  onStylesLoaded?: (count: number) => void;
}) => {
  if (stylesLoaded) return;

  // Resolve baseUrl: prefer provided, then federation config, then env var, finally fallback
  let baseUrl = options?.baseUrl;

  if (!baseUrl) {
    try {
      // Try to get from federation config
      const { federationConfigLoader, resolveRemoteUrl } = await import(
        '@/lib/moduleFederation/config/federationConfigLoader'
      );
      const remoteConfig = federationConfigLoader.getRemoteConfig('aga-components');

      if (remoteConfig) {
        const { url } = resolveRemoteUrl(remoteConfig);
        baseUrl = url.replace('/remoteEntry.js', '').replace(/\/$/, '');
      }
    } catch (error) {
      console.warn('[loadFederatedStyles] Failed to load federation config, falling back to env var:', error);
    }

    // Fallback to env var or default
    baseUrl = baseUrl || env('NEXT_PUBLIC_AGA_COMPONENTS_URL') || 'http://localhost:3003';
  }

  baseUrl = baseUrl.replace(/\/$/, '');
  let loadedCount = 0;

  try {
    const manifestResponse = await fetch(`${baseUrl}/css-manifest.json`);
    const manifest = await manifestResponse.json();

    const cssFiles: string[] = Array.isArray(manifest?.css) ? manifest.css : manifest?.css ? [manifest.css] : [];

    for (const cssFile of cssFiles) {
      if (!cssFile) continue;
      const cssUrl = `${baseUrl}/${cssFile}`;
      if (!document.querySelector(`link[href="${cssUrl}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
        loadedCount++;
      }
    }
  } catch (err) {
    console.error('Failed to load AGA Components CSS manifest:', err);
    // Do not throw to avoid breaking first paint; app styles may still load below
  }

  // Load additional app-specific CSS files
  const additional = options?.additionalStyles || [];
  for (const src of additional) {
    if (src.type !== 'css-file' || !src.url) continue;
    if (!document.querySelector(`link[href="${src.url}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = src.url;
      if (src.crossOrigin) link.crossOrigin = src.crossOrigin;
      document.head.appendChild(link);
      loadedCount++;
    }
  }

  if (loadedCount > 0) {
    stylesLoaded = true;
    options?.onStylesLoaded?.(loadedCount);
    console.log(`Loaded ${loadedCount} CSS files (federated + additional)`);
  }
};
